import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';

export interface TabContainerHeaderDividerProps {
  className?: string;
}

export function TabContainerHeaderDivider(props: TabContainerHeaderDividerProps): JSX.Element {
  return <hr className={classNames('tabContainer-headerDivider', props.className)} />;
}

interface NavItemProps<T extends string> {
  active: boolean;
  tabKey: T;
  title: ReactNode;
  centerNavItem?: boolean;
  onClick: (tabKey: T) => void;
}

export function NavItem<T extends string>(props: NavItemProps<T>): JSX.Element {
  function handleClick(): void {
    props.onClick(props.tabKey);
  }

  return (
    <div
      className={classNames('tabContainer-navItem', {
        [css(styles.centeredNavItem)]: props.centerNavItem,
        'tabContainer-navItem--active': props.active,
      })}
      onClick={handleClick}
      {...getTestProps({component: 'NavItem', key: `${props.tabKey}`})}
    >
      {props.title}
    </div>
  );
}

NavItem.propTypes = {
  active: PropTypes.bool.isRequired,
  tabKey: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
};

export interface TabContainerProps<T extends string> {
  activeTab: T;
  children: ReactNode;
  className?: string;
  isFullHeight: boolean;
  onRequestTabChange: (tabKey: T) => void;
  navClassName?: string;
  paneClassName?: string;
  headerDividerClassName?: string;
  centerNavItem?: boolean;
}

/*
 * TabContainer accepts tabKey on each Tab, and a defaultKey to specify which Tab to show first.
 * You can leave off tabKey and defaultKey to just show the first tab by default.
 */
export function TabContainer<T extends string>(props: TabContainerProps<T>): JSX.Element {
  function isActiveTab(tab: React.ReactElement<TabProps<T>>): boolean {
    return tab.props.tabKey === props.activeTab;
  }

  function handleSwitchTab(tabKey: T): void {
    props.onRequestTabChange(tabKey);
  }

  // Filter out null/undefined children
  const tabArray = React.Children.toArray(props.children).filter((child) => child) as Array<
    React.ReactElement<TabProps<T>>
  >;
  const [activeTab] = tabArray.filter((tab, idx) => isActiveTab(tab));
  return (
    <div className={props.className} {...getTestProps({component: 'TabContainer', element: 'root'})}>
      <div className={classNames('tabContainer-nav', props.navClassName)}>
        {tabArray.map((child, idx) => (
          <NavItem
            key={child.props.tabKey}
            active={isActiveTab(child)}
            tabKey={child.props.tabKey}
            title={child.props.title}
            onClick={handleSwitchTab}
            centerNavItem={props.centerNavItem}
          />
        ))}
      </div>
      <TabContainerHeaderDivider className={props.headerDividerClassName} />
      <div
        className={classNames('tabContainer-pane', props.paneClassName, {
          'u-flex-full-height': props.isFullHeight,
        })}
        {...getTestProps({component: 'TabContainer', element: 'pane'})}
      >
        {activeTab.props.children}
      </div>
    </div>
  );
}

TabContainer.defaultProps = {
  isFullHeight: false,
};

TabContainer.propTypes = {
  activeTab: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  // Apply the class u-flex-full-height, which makes the tab container grow to fill its parent
  // and allows children to grow to fill the tab container
  isFullHeight: PropTypes.bool.isRequired,
  onRequestTabChange: PropTypes.func.isRequired,
};

interface TabProps<T> {
  children: ReactNode;
  tabKey: T;
  title: ReactNode;
}

export function Tab<T>(props: TabProps<T>): JSX.Element {
  throw new Error('Tab should not be actually rendered and is only used for external API');
}

Tab.propTypes = {
  tabKey: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
};

const styles = StyleSheet.create({
  centeredNavItem: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    marginRight: 0,
  },
});
